import React from "react";
import { formatDecimalNumber } from "../../config_and_helpers/helpers";
import { ArrowDownShort, ArrowUpShort } from "react-bootstrap-icons";
import "./GenericComponentsStyle.css";

/**
 * PriceDifferenceComponent is a React component that displays the price difference between two products and an icon based on the difference.
 *
 * @typedef {Object} PriceDifferenceComponentProps
 * @property {number} baseProductPricePerUnit - The base product price per unit.
 * @property {number} comparedPricePerUnit - The compared product price per unit.
 * @property {boolean} productHasSubUnits - Whether the product has sub units or not.
 * @property {string} productUnit - The unit of the product.
 */
const PriceDifferenceComponent = ({
  baseProductPricePerUnit,
  comparedPricePerUnit,
  productHasSubUnits,
  productUnit,
}) => {
  const perUnitDelta = formatDecimalNumber(
    comparedPricePerUnit - baseProductPricePerUnit,
  );

  /**
   * Function that returns a React component displaying an icon and text based on the price difference.
   *
   * @returns {React.Component} A React component displaying an icon and text.
   */
  const priceDifferenceIcon = (deltaType) => {
    let resultIcon;
    let resultText;

    // if delta is less than 0 (price dropped), set the icon as green down arrow and text as "Better Price!"
    if (deltaType < 0) {
      resultIcon = (
        <ArrowDownShort color="green" size={"1.6rem"} className="me-1" />
      );
      resultText = <span style={{ color: "green" }}>Better Price!</span>;

      // if delta is greater than 0 (price increased), set the icon as red up arrow
    } else if (deltaType > 0) {
      resultIcon = (
        <ArrowUpShort color={"red"} size={"1.6rem"} className={"me-1"} />
      );

      // if delta is equal to 0 (prices are equal), set the text as "Same Price"
    } else {
      resultIcon = null;
      resultText = <span>Same Price</span>;
    }

    // returns a JSX component containing the icon and text
    return (
      <div
        className={
          "d-flex align-middle align-items-center justify-content-center"
        }
      >
        {resultIcon}
        {resultText}
      </div>
    );
  };

  return (
    <div
      className={
        "mt-2 d-flex flex-column align-middle align-items-center bordered-shadow-container bg-light d-inline"
      }
    >
      <div className={"d-flex flex-row align-middle align-items-center"}>
        <span className={"me-2"}>
          {`${
            productHasSubUnits
              ? `Price Per Smallest Unit`
              : `Price Per Unit (${productUnit})`
          }:`}{" "}
          {perUnitDelta}
        </span>
        {priceDifferenceIcon(perUnitDelta)}
      </div>
    </div>
  );
};

export default PriceDifferenceComponent;
